import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Save, Sparkles, Wand2, Upload, FileText, ImagePlus, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media";

// Parse a Markdown file's contents into { title, content }.
// If the first non-empty line starts with `# `, that becomes the title
// and is stripped from the content. Otherwise title stays empty.
function parseMarkdownFile(text) {
  const raw = (text || "").replace(/^\uFEFF/, ""); // strip BOM
  const lines = raw.split(/\r?\n/);
  // Skip leading blank lines
  let idx = 0;
  while (idx < lines.length && !lines[idx].trim()) idx++;
  let title = "";
  if (idx < lines.length && lines[idx].startsWith("# ")) {
    title = lines[idx].slice(2).trim();
    idx++;
    // Also skip blank lines directly after H1
    while (idx < lines.length && !lines[idx].trim()) idx++;
  }
  const content = lines.slice(idx).join("\n").trim();
  return { title, content };
}

export default function AdminPostEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    content: "",
    cover_image: "",
    tags: "",
    published: true,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);

  // AI panel
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiOutline, setAiOutline] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");

  // Uploads
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverUploadError, setCoverUploadError] = useState("");
  const [mdFileName, setMdFileName] = useState("");
  const [mdError, setMdError] = useState("");

  const onCoverFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-uploading the same filename later
    if (!file) return;
    setCoverUploadError("");
    if (!file.type || !file.type.startsWith("image/")) {
      setCoverUploadError("Bitte eine Bilddatei auswählen (PNG, JPG, WebP, GIF, SVG).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setCoverUploadError("Datei zu groß (max 8 MB).");
      return;
    }
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd);
      setForm((f) => ({ ...f, cover_image: data.url }));
    } catch (err) {
      setCoverUploadError(formatApiError(err));
    } finally {
      setCoverUploading(false);
    }
  };

  const onMarkdownFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setMdError("");
    const nameOk = /\.(md|markdown|txt)$/i.test(file.name);
    if (!nameOk && !(file.type || "").startsWith("text/")) {
      setMdError("Bitte eine .md, .markdown oder .txt Datei auswählen.");
      return;
    }
    try {
      const text = await file.text();
      const { title, content } = parseMarkdownFile(text);
      setForm((f) => ({
        ...f,
        title: title || f.title,
        content: content || f.content,
      }));
      setMdFileName(file.name);
    } catch (err) {
      setMdError("Datei konnte nicht gelesen werden: " + (err?.message || "unbekannter Fehler"));
    }
  };

  useEffect(() => {
    if (isNew) return;
    api
      .get("/blog/all")
      .then((r) => {
        const p = (r.data || []).find((x) => x.id === id);
        if (p) {
          setForm({
            title: p.title,
            excerpt: p.excerpt,
            content: p.content,
            cover_image: p.cover_image || "",
            tags: (p.tags || []).join(", "),
            published: p.published,
          });
        } else {
          setError("Beitrag nicht gefunden.");
        }
      })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const onSave = async () => {
    setSaving(true);
    setError("");
    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      content: form.content,
      cover_image: form.cover_image.trim() || null,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      published: form.published,
    };
    try {
      if (isNew) {
        const res = await api.post("/blog", payload);
        navigate(`/admin/posts/${res.data.id}`, { replace: true });
      } else {
        await api.put(`/blog/${id}`, payload);
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const runAI = async () => {
    if (!aiTopic.trim()) return;
    setAiBusy(true);
    setAiError("");
    try {
      const { data } = await api.post("/ai/draft-blog", {
        topic: aiTopic.trim(),
        outline: aiOutline.trim() || null,
        language: "de",
      });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        excerpt: data.excerpt || f.excerpt,
        content: data.content || f.content,
        tags: (data.tags && data.tags.length ? data.tags.join(", ") : f.tags),
      }));
      setAiOpen(false);
    } catch (e) {
      setAiError(formatApiError(e));
    } finally {
      setAiBusy(false);
    }
  };

  if (loading) {
    return <div className="max-w-[1200px] mx-auto px-6 py-32 text-center text-[var(--text-2)]">Laden…</div>;
  }

  return (
    <div data-testid="admin-editor" className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 pb-24">
      <Helmet>
        <title>{`${isNew ? "New post" : "Edit post"} | Admin · Karim Benna IT Consulting`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-mono text-[var(--text-2)] hover:text-[var(--gold)] mb-4"
          >
            <ArrowLeft size={14} /> Dashboard
          </Link>
          <h1 className="font-display text-3xl lg:text-4xl tracking-tight">
            {isNew ? "Neuer Beitrag" : "Beitrag bearbeiten"}
          </h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <label
            className="btn-ghost text-xs cursor-pointer"
            data-testid="md-upload-label"
            title="Markdown-Datei laden (füllt Titel & Inhalt automatisch)"
          >
            <FileText size={14} /> Markdown laden
            <input
              type="file"
              accept=".md,.markdown,.txt,text/markdown,text/plain"
              onChange={onMarkdownFileSelected}
              className="hidden"
              data-testid="md-upload-input"
            />
          </label>
          <button
            onClick={() => setAiOpen((v) => !v)}
            data-testid="ai-toggle"
            className="btn-ghost text-xs"
          >
            <Wand2 size={14} /> KI-Entwurf
          </button>
          <button
            onClick={() => setPreview((v) => !v)}
            data-testid="preview-toggle"
            className="btn-ghost text-xs"
          >
            {preview ? "Editor" : "Vorschau"}
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="post-save"
            className="btn-primary text-xs disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="editor-error" className="border border-red-500/40 text-red-400 p-3 mb-6 text-sm">
          {error}
        </div>
      )}

      {mdError && (
        <div data-testid="md-error" className="border border-red-500/40 text-red-400 p-3 mb-6 text-sm">
          {mdError}
        </div>
      )}

      {mdFileName && !mdError && (
        <div data-testid="md-loaded" className="border border-emerald-500/40 text-emerald-600 p-3 mb-6 text-xs font-mono flex items-center gap-2">
          <FileText size={14} /> Geladen: {mdFileName} — Titel &amp; Inhalt wurden befüllt.
        </div>
      )}

      {aiOpen && (
        <div data-testid="ai-panel" className="border hairline p-6 mb-8 bg-[var(--bg-1)]/40">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles size={16} className="text-[var(--gold)]" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--gold)]">
              KI-Entwurf (Claude Sonnet 4.5)
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-2)]">
                Thema *
              </span>
              <input
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                data-testid="ai-topic"
                placeholder="z. B. Microservices-Patterns mit Spring Boot"
                className="mt-2 w-full bg-transparent border-b hairline focus:border-[var(--gold)] outline-none py-2 text-[var(--text-0)]"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-2)]">
                Gliederung / Hinweise (optional)
              </span>
              <input
                value={aiOutline}
                onChange={(e) => setAiOutline(e.target.value)}
                data-testid="ai-outline"
                placeholder="3 Hauptkapitel, praktisches Beispiel mit Code"
                className="mt-2 w-full bg-transparent border-b hairline focus:border-[var(--gold)] outline-none py-2 text-[var(--text-0)]"
              />
            </label>
          </div>
          {aiError && (
            <div className="text-xs text-red-400 mt-3" data-testid="ai-error">{aiError}</div>
          )}
          <div className="flex justify-end mt-5">
            <button
              onClick={runAI}
              disabled={aiBusy || !aiTopic.trim()}
              data-testid="ai-generate"
              className="btn-primary text-xs disabled:opacity-50"
            >
              {aiBusy ? "Generiere..." : "Artikel generieren"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <Field
            label="Titel"
            testid="post-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
          <Field
            label="Teaser / Excerpt"
            testid="post-excerpt"
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            textarea
            rows={3}
            required
          />

          {preview ? (
            <div className="border hairline p-8 prose-karim min-h-[400px]">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content || "*Noch kein Inhalt*"}</ReactMarkdown>
            </div>
          ) : (
            <Field
              label="Inhalt (Markdown)"
              testid="post-content"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              textarea
              rows={28}
              mono
              required
            />
          )}
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div className="border hairline p-5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="post-published"
                className="accent-[var(--gold)]"
              />
              <span className="text-[var(--text-0)]">Veröffentlicht</span>
            </label>
          </div>
          <div className="border hairline p-5 space-y-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-2)] mb-2">
                Cover-Bild
              </div>
              <label
                className="btn-ghost text-xs w-full justify-center cursor-pointer"
                data-testid="cover-upload-label"
              >
                {coverUploading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Lädt hoch…
                  </>
                ) : (
                  <>
                    <ImagePlus size={14} /> Bild hochladen
                  </>
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  onChange={onCoverFileSelected}
                  disabled={coverUploading}
                  className="hidden"
                  data-testid="cover-upload-input"
                />
              </label>
              {coverUploadError && (
                <div className="mt-2 text-xs text-red-500" data-testid="cover-upload-error">
                  {coverUploadError}
                </div>
              )}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-3)] text-center">
              oder URL einfügen
            </div>
            <Field
              label="Cover Image URL"
              testid="post-cover"
              value={form.cover_image}
              onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
              placeholder="https://... oder /api/media/..."
            />
          </div>
          {form.cover_image && (
            <div className="border hairline overflow-hidden">
              <img
                src={resolveMediaUrl(form.cover_image)}
                alt="cover"
                className="w-full"
                data-testid="cover-preview"
              />
            </div>
          )}
          <Field
            label="Tags (komma-getrennt)"
            testid="post-tags"
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            placeholder="Engineering, Spring Boot, Mindset"
          />
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, required, textarea, rows = 4, mono, placeholder }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-2)]">
        {label}
        {required && <span className="text-[var(--gold)] ml-1">*</span>}
      </span>
      {textarea ? (
        <textarea
          rows={rows}
          value={value}
          onChange={onChange}
          required={required}
          data-testid={testid}
          placeholder={placeholder}
          className={`mt-2 w-full bg-transparent border hairline focus:border-[var(--gold)] outline-none p-3 text-[var(--text-0)] resize-y ${mono ? "font-mono text-sm" : ""}`}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={onChange}
          required={required}
          data-testid={testid}
          placeholder={placeholder}
          className="mt-2 w-full bg-transparent border-b hairline focus:border-[var(--gold)] outline-none py-2 text-[var(--text-0)]"
        />
      )}
    </label>
  );
}
